<template>
  <div v-if="!todoItem">
    <div class="tw-text-center tw-text-gray-500 tw-py-6">Loading Activity...</div>
  </div>
  <div v-else>
    <div class="tw-flex tw-justify-between tw-items-center tw-mb-4">
      <h2
        class="tw-text-xl sm:tw-text-xl tw-text-primary-500 tw-bg-transparent tw-shadow-none tw-font-sans tw-inline-flex tw-items-center border-0 focus:tw-outline-none focus:tw-text-primary-700"
      >
        Activity Submission
      </h2>
    </div>

    <div class="tw-mb-2 tw-text-right">
      <base-button type="text" size="sm" @click="viewLastSubmittedOrder">View Last Submitted Orders</base-button>
    </div>

    <base-table ref="todoItems" :fields="fields" :items="hasData ? [todoItem] : []" :overflowVisible="false">
      <!-- Due Date -->
      <template #dueDate="{ item: todoItem }">
        <div class="tw-flex tw-items-center tw-text-sm tw-whitespace-nowrap">
          <div :class="'tw-text-' + dueDateColor(todoItem.dueDate) + '-500'">{{ dueDateFormatter(todoItem.dueDate) }}</div>
        </div>
        <div v-if="todoItem.status === 'completed'" class="tw-text-xs tw-text-gray-500">
          <div class="tw-flex tw-items-center tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">
            <span>Completed By:</span>
          </div>
          <div class="tw-flex tw-items-center tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">
            <span>{{ todoItem.insertBy }}</span>
          </div>
          <div class="tw-flex tw-items-center tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">
            <span> ({{ todoItem.completedDate }}) </span>
          </div>
        </div>
      </template>

      <!-- Todo Item -->
      <template #todoItem="{ item: todoItem }">
        <div class="tw-flex">
          <div>
            <div class="tw-flex tw-flex-col tw-items-start">
              <div class="tw-flex">
                <div class="tw-flex tw-font-normal tw-text-gray-700 tw-text-base tw-whitespace-nowrap">{{ todoItem.type.label }}</div>
                <base-badge v-if="todoItem.isDeleted" class="tw-ml-2" size="sm" color="red">Archived</base-badge>
              </div>
              <div class="tw-flex tw-font-normal tw-text-xs tw-whitespace-nowrap">
                <base-button type="text" size="md" @click="gotoMember(todoItem.userId)">{{ todoItem.user.fullName }}</base-button>
              </div>
              <div v-if="todoItem.businessLocation.name" class="tw-flex tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">{{ todoItem.businessLocation.name }}</div>
              <span v-else class="tw-text-xs tw-text-red-500">No Location Associated</span>
              <div v-if="todoItem.businessPrescription.programName" class="tw-flex tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">
                {{ todoItem.businessPrescription.programName }}
              </div>
              <div v-if="todoItem.userBusinessPlan.name" class="tw-flex tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">
                {{ todoItem.userBusinessPlan.name }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Actions -->
      <template #actions="{ item: todoItem }">
        <div class="tw-flex tw-justify-start tw-space-x-2">
          <base-button type="flat" size="sm" @click="history(todoItem.id, todoItem.isDeleted, todoItem.businessPrescriptionId)">
            <template #icon>
              <list-icon />
            </template>
            <span class="tw-whitespace-nowrap">
              {{ todoItem.todoItemTypeLabel === "Prescriber Clarification" && isLocationFiltered && todoItem.status !== "completed" ? "Action Needed" : "Details" }}
            </span>
            <base-badge v-show="todoItem.numComments > 0 && needsDetailsCheck" class="tw-absolute tw--right-2 tw--top-0.5" color="primary" size="xs">
              {{ todoItem.numComments }}
            </base-badge>
          </base-button>
        </div>
      </template>

      <!-- Empty State -->
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-items-center tw-py-5 tw-text-sm">
          <span v-if="error" class="tw-text-red-500">{{ error }}</span>
          <span v-else class="tw-text-gray-500">No Results</span>
        </div>
      </template>

      <!-- Loading Placeholder -->
      <template #loading-row>
        <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-6 tw-w-12" /></td>
        <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-6 tw-w-12" /></td>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex tw-space-x-2 tw-justify-center">
            <base-skeleton-loader class="tw-h-8 tw-w-20" v-for="key in 4" :key="`action-loader-cell-${key}`" />
          </div>
        </td>
      </template>
    </base-table>
  </div>
</template>

<script>
import moment from "moment";
import { ListIcon } from "@/assets/svgs";

export default {
  name: "ActivitySubmission",
  props: {
    businessId: String,
    todoItem: {
      type: Object,
      default: () => ({}),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: [String, Boolean],
      default: null,
    },
    needsDetailsCheck: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ListIcon,
  },
  computed: {
    fields() {
      return [
        { title: "Due Date", key: "dueDate", thClass: "tw-w-0 tw-text-left tw-whitespace-nowrap" },
        { title: "Activity", key: "todoItem", thClass: "tw-w-0 tw-text-left" },
        { title: "Action", key: "actions", thClass: "tw-w-0 tw-text-left" },
      ];
    },
    hasData() {
      return !!this.todoItem && Object.keys(this.todoItem).length > 0 && !this.error;
    },
  },
  methods: {
    viewLastSubmittedOrder() {
      this.$emit("viewSubmittedOrder");
    },
    history(todoItemId, isDeleted, businessPrescriptionId) {
      this.$emit("history", { todoItemId, isDeleted, businessPrescriptionId });
    },
    dueDateFormatter(date) {
      if (!date) return "";
      return moment(date).format("MM/DD/YY");
    },
    dueDateColor(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffDays = moment(date).diff(today, "days");

      if (moment(date).isSame(today, "day")) return "black";
      if (diffDays > 0) return "primary";
      return "red";
    },
    gotoMember(memberId) {
      window.open(`/businesses/${this.businessId}/members/${memberId}/plans`, "_blank");
    },
  },
};
</script>
